import { api } from "../lib/axios";

/**
 * Public white-label portal config, resolved from a request hostname.
 *
 * Backed by `GET /white-label/resolve?host=` — unauthenticated on purpose, so
 * the login/signup pages can brand themselves before anyone has a token. The
 * backend answers null for anything that is not a live brand portal (the
 * marketplace apex, localhost, an unknown or switched-off slug), and the
 * fallback to the brand's own identity happens server-side, so `portalName`
 * is always usable when a config comes back at all.
 */
/** What a hostname turned out to be. Mirrors the backend's HostKind. */
export type HostKind = "MARKETPLACE" | "PORTAL" | "UNKNOWN_PORTAL";

export interface PublicPortalConfig {
  kind: "PORTAL";
  brandDetailId: string;
  brandUsername: string | null;
  /** The brand's own name. There is no separate portal name. */
  brandName: string | null;
  portalSlug: string | null;
  portalLogo: string | null;
  /**
   * The brand's accent colours from its Appearance settings. Null unless the
   * portal's tier carries BRAND_COLOURS (FULL) — a BASIC portal is name and
   * logo only and keeps the default scale.
   */
  primaryColour: string | null;
  secondaryColour: string | null;
  tier: "BASIC" | "FULL";
  capabilities: string[];
}

export type HostResolution =
  | { kind: "MARKETPLACE" }
  | { kind: "UNKNOWN_PORTAL"; portalSlug: string }
  | PublicPortalConfig;

export const portalBrandingService = {
  async resolveByHost(host: string): Promise<HostResolution> {
    const { data } = await api.get<HostResolution | "">("/white-label/resolve", {
      params: { host },
    });
    // An older backend answered null/empty for everything that was not a
    // portal. Treat that as the marketplace so a version skew degrades to the
    // previous behaviour rather than 404-ing every page.
    if (!data || typeof data !== "object" || !("kind" in data)) {
      return { kind: "MARKETPLACE" };
    }
    return data;
  },

  /**
   * Public config for one brand, by BrandDetail id — used by the branded
   * join/signup flow (ticket 5) so a brand's join link shows the brand's
   * portal identity through signup. Null unless the portal is actually live.
   */
  async getPublicConfigForBrand(
    brandDetailId: string
  ): Promise<PublicPortalConfig | null> {
    const { data } = await api.get<PublicPortalConfig | null | "">(
      `/white-label/brands/${encodeURIComponent(brandDetailId)}/public`
    );
    return data && typeof data === "object" && data.brandDetailId ? data : null;
  },
};
